import { useMemo } from "react";

import {
  Bar,
  Bubble,
  HorizontalBar,
  Line,
  Pie,
  Doughnut
} from "react-chartjs-2";

export default function Charts({
  type,
  title,
  legendX,
  legendY,
  maxScaleStep = 5,
  maxScaleValue,
  ...props
}) {
  const maxDataValue = useMemo(() => {
    if (props.data.datasets.length === 0) return 0;
    return Math.max(
      ...props.data.datasets.map(dataset => Math.max(...dataset.data))
    );
  }, [props.data.datasets]);

  const ceiling = useMemo(() => {
    if (maxScaleValue) return maxScaleValue;

    let ceil = Math.ceil(maxDataValue / maxScaleStep) * maxScaleStep;
    if (maxDataValue === maxScaleStep) ceil *= 1.5;
    return ceil > 10 ? ceil : 10;
  }, [maxDataValue, maxScaleStep]);

  return (
    <>
      {type === "horizontalbar" ? (
        <HorizontalBar
          options={{
            title: {
              display: true,
              text: title,
              fontSize: 20
            },
            tooltips: {
              callbacks: {
                label(value) {
                  return new Intl.NumberFormat("en-US", {
                    maximumSignificantDigits: 2
                  }).format(value.value);
                }
              }
            },
            legend: {
              display: false,
              labels: {
                font: {
                  size: 18
                }
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendY,
                    fontSize: 18
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendX,
                    fontSize: 18
                  }
                }
              ]
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "horizontalbar2" ? (
        <HorizontalBar
          options={{
            title: {
              display: true,
              text: title,
              fontSize: 20
            },
            legendY: {
              labels: {
                display: false
              }
            },
            legend: {
              display: true,
              labels: {
                font: {
                  size: 18
                }
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendY,
                    fontSize: 16
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendX,
                    fontSize: 16
                  }
                }
              ]
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "bar" ? (
        <Bar
          options={{
            title: {
              display: true,
              text: title,
              fontSize: 20
            },
            plugins: {
              decimation: {
                enabled: false
              }
            },
            legend: {
              display: true,
              labels: {
                font: {
                  size: 18
                }
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    max: ceiling,
                    maxTicksLimit: ceiling + 1
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendY,
                    fontSize: 18
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendX,
                    fontSize: 18
                  }
                }
              ]
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "bar2" ? (
        <Bar
          options={{
            tooltips: { enabled: false },
            hover: { mode: null },
            title: {
              display: true,
              text: title,
              fontSize: 20
            },
            legend: {
              display: true,
              labels: {
                font: {
                  size: 18
                }
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    max: ceiling,
                    maxTicksLimit: ceiling + 1
                  },
                  scaleLabel: {
                    fontSize: 18,
                    display: true,
                    labelString: legendY
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    fontSize: 18,
                    display: true,
                    labelString: legendX
                  }
                }
              ]
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "bubble" ? (
        <Bubble
          options={{
            responsive: true,
            title: {
              display: false,
              text: title,
              fontSize: 20
            },
            legend: {
              position: "bottom"
            },
            layout: {
              padding: {
                left: 15,
                top: 15,
                right: 15,
                bottom: 15
              }
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    display: true
                  },
                  afterTickToLabelConversion(q) {
                    for (let tick in q.ticks) {
                      q.ticks[tick] = `Q${q.ticks.length - parseInt(tick)}`;
                    }
                  },
                  gridLines: {
                    drawBorder: true,
                    display: true
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true,
                    display: true
                  },
                  afterTickToLabelConversion(q) {
                    for (let tick in q.ticks) {
                      q.ticks[tick] = String.fromCharCode(65 + parseInt(tick));
                    }
                  },
                  gridLines: {
                    drawBorder: true,
                    display: true
                  }
                }
              ]
            },
            tooltips: {
              callbacks: {
                label(tooltipItem, data) {
                  let rLabel =
                    data.datasets[tooltipItem.datasetIndex].data[
                      tooltipItem.index
                    ].r;
                  return rLabel;
                }
              }
            },
            ...props.addOptions
          }}
          {...props}
        />
      ) : type === "pie" ? (
        <Pie
          options={{
            responsive: true,
            title: {
              display: true,
              text: title,
              fontSize: 20
            }
          }}
        />
      ) : type === "line" ? (
        <Line
          options={{
            responsive: true,
            title: {
              display: true,
              text: title,
              fontSize: 18
            },
            scales: {
              yAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendY
                  }
                }
              ],
              xAxes: [
                {
                  ticks: {
                    beginAtZero: true
                  },
                  scaleLabel: {
                    display: true,
                    labelString: legendX
                  }
                }
              ]
            },
            legend: {
              display: false
            }
          }}
          {...props}
        />
      ) : type === "doughnut " ? (
        <Doughnut {...props} />
      ) : null}
    </>
  );
}
